/* tutor-1 (C): LA RESPUESTA DEL TUTOR, SIN BURBUJA: la chispa de 24 a la izquierda y el texto a todo lo ancho
   (15 px, interlineado 1.62). El pie: "Mostrar fuentes (n)" / "Ocultar fuentes" (solo con fuentes), copiar, y los
   pulgares (mandan la opinion y se pintan activos). La tarjeta de fuentes: un renglon por fuente, con el cuadrito (la
   inicial del dominio o el icono de documento o de clip), el titulo, la etiqueta y debajo la pagina o el dominio; las
   de la web abren en otra pestana.

   camara-pills remate (firma #720): la chispa sube a SU renglon (24 de alto, encima del texto) y el texto, el pie y la
   tarjeta de fuentes usan TODO el ancho de la columna (antes la chispa ocupaba una columna de 24 mas 12 de separacion:
   36 px menos de texto en las tres superficies).

   ARCHIVO EN ASCII A PROPOSITO. */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BarChart3, ChevronDown, Copy, FileText, Paperclip, ThumbsDown, ThumbsUp } from 'lucide-react';
import Chispa from './Chispa';
import Marcado from './Marcado';
import { AreaDe44 } from '../panel/anatomia';

const BOTON_FANTASMA = 'relative inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-[9px] text-[13px] font-medium text-[#9A9A9A] hover:bg-[#1B1B1B] hover:text-[#C8C8C8]';
const BOTON_ICONO = 'relative w-[30px] h-[30px] rounded-[9px] flex items-center justify-center text-[#9A9A9A] hover:bg-[#1B1B1B] hover:text-[#C8C8C8]';

function debajoDe(f, t) {
  if (f.tipo === 'web') return f.detalle || '';
  const d = String(f.detalle || '');
  let m = /^(\d+)$/.exec(d);
  if (m) return t('tutor.sources.page', { p: m[1] });
  m = /^(\d+),(\d+)$/.exec(d);
  if (m) return t('tutor.sources.pages', { a: m[1], b: m[2] });
  return d;
}

function Cuadrito({ fuente }) {
  const clase = "w-7 h-7 rounded-lg bg-[#1B1B1B] border border-[#2C2C2C] flex items-center justify-center text-[#C8C8C8] text-[12px] font-bold font-['Space_Grotesk']";
  if (fuente.tipo === 'documento') return <span className={clase} aria-hidden="true"><FileText className="w-3.5 h-3.5" /></span>;
  if (fuente.tipo === 'subido') return <span className={clase} aria-hidden="true"><Paperclip className="w-3.5 h-3.5" /></span>;
  // tutor-2 (A3.c, D.3): "Tus analiticas" (el servidor no escribe texto de usuario: el titulo lo pone el cliente)
  if (fuente.tipo === 'analiticas') return <span className={clase} aria-hidden="true"><BarChart3 className="w-3.5 h-3.5" /></span>;
  const inicial = String(fuente.detalle || fuente.titulo || '?').trim().charAt(0).toUpperCase() || '?';
  return <span className={clase} aria-hidden="true">{inicial}</span>;
}

export function TarjetaDeFuentes({ fuentes }) {
  const { t } = useTranslation('study');
  const etiqueta = { documento: 'tutor.sources.doc', subido: 'tutor.sources.uploaded', web: 'tutor.sources.web', analiticas: 'tutor.sources.analytics' };
  return (
    <div className="rounded-[14px] bg-[#101010] border border-[#1F1F1F] px-3.5 py-3 grid gap-2.5" data-testid="tutor-fuentes">
      <div className="text-[11px] tracking-[.06em] uppercase text-[#6F6F6F] font-semibold">{t('tutor.sources.title')}</div>
      {fuentes.map((f, i) => {
        const dentro = (
          <>
            <Cuadrito fuente={f} />
            <span className="min-w-0">
              <span className="block text-[14px] leading-[1.3] text-[#F4F4F3] truncate">
                {f.tipo === 'analiticas' ? t('tutor.sources.analytics') : f.titulo}
                {f.tipo === 'analiticas' ? null : <span className="ml-1.5 text-[11px] text-[#6F6F6F]">{t(etiqueta[f.tipo] || etiqueta.web)}</span>}
              </span>
              {debajoDe(f, t) ? <span className="block text-[12px] text-[#9A9A9A] truncate">{debajoDe(f, t)}</span> : null}
            </span>
          </>
        );
        const clase = 'grid grid-cols-[28px_1fr] gap-2.5 items-center min-w-0';
        return f.tipo === 'web' && f.url ? (
          <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" className={`${clase} hover:opacity-90`} data-testid="tutor-fuente">{dentro}</a>
        ) : (
          <div key={i} className={clase} data-testid="tutor-fuente">{dentro}</div>
        );
      })}
    </div>
  );
}

export default function RespuestaTutor({ mensaje, alOpinar, alAbrirFuentes, escribiendo = false }) {
  const { t } = useTranslation('study');
  const [conFuentes, setConFuentes] = useState(false);
  const fuentes = (mensaje && mensaje.fuentes) || [];
  const opinion = mensaje && mensaje.opinion;
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(mensaje.texto || '');
      toast.success(t('tutor.answer.copied'));
    } catch (e) {
      /* sin portapapeles: nada que decir */
    }
  };
  const alternarFuentes = () => {
    if (!conFuentes && alAbrirFuentes) alAbrirFuentes();
    setConFuentes((v) => !v);
  };
  return (
    <div className="min-w-0" data-testid="tutor-respuesta" data-id={mensaje && mensaje.id}>
      <span className="flex h-6 mb-2" data-testid="tutor-respuesta-chispa"><Chispa /></span>
      <div className="min-w-0 grid gap-3">
        <Marcado texto={(mensaje && mensaje.texto) || ''} />
        {escribiendo ? null : (
          <div className="flex items-center gap-0.5 -mt-0.5" data-testid="tutor-pie">
            {fuentes.length ? (
              <button type="button" className={BOTON_FANTASMA} onClick={alternarFuentes} aria-expanded={conFuentes}
                data-testid="tutor-mostrar-fuentes">
                <AreaDe44 />
                {conFuentes ? t('tutor.answer.hide_sources') : t('tutor.answer.show_sources', { n: fuentes.length })}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform${conFuentes ? ' rotate-180' : ''}`} aria-hidden="true" />
              </button>
            ) : null}
            <button type="button" className={BOTON_ICONO} onClick={copiar} aria-label={t('tutor.answer.copy')} title={t('tutor.answer.copy')}
              data-testid="tutor-copiar">
              <AreaDe44 />
              <Copy className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
            <button type="button" className={`${BOTON_ICONO}${opinion === 'up' ? ' text-[#F7A06A]' : ''}`} aria-pressed={opinion === 'up'}
              aria-label={t('tutor.answer.good')} title={t('tutor.answer.good')}
              onClick={() => alOpinar && alOpinar(opinion === 'up' ? null : 'up')} data-testid="tutor-pulgar-arriba">
              <AreaDe44 />
              <ThumbsUp className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
            <button type="button" className={`${BOTON_ICONO}${opinion === 'down' ? ' text-[#F7A06A]' : ''}`} aria-pressed={opinion === 'down'}
              aria-label={t('tutor.answer.bad')} title={t('tutor.answer.bad')}
              onClick={() => alOpinar && alOpinar(opinion === 'down' ? null : 'down')} data-testid="tutor-pulgar-abajo">
              <AreaDe44 />
              <ThumbsDown className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          </div>
        )}
        {conFuentes && fuentes.length ? <TarjetaDeFuentes fuentes={fuentes} /> : null}
      </div>
    </div>
  );
}
